import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Select, Spin} from 'antd';
import _ from 'lodash';

/**
 * Selects whose options come a page at a time.
 *
 * Filter panels had been fetching whole option lists up front — instruments
 * capped at `limit: 100` behind a 1.2s debounce, collections with
 * `{all: true}` — so anything past the first page was unreachable and the
 * only way to pick it was to already know its name.
 *
 * `usePaginatedOptions` owns the paging and the debounced server search;
 * `PaginatedSelect` renders it. A picked value is kept in a local cache so an
 * option chosen on page four, or loaded from a saved view, still renders its
 * label after the list moves on.
 */

const SEARCH_DEBOUNCE = 350;

export const usePaginatedOptions = ({
  query,
  /** (page, search) => variables */
  buildVariables,
  /** data => ({rows, hasNextPage}) */
  readPage,
  /** row => ({value, label, ...extras}) */
  toOption,
  skip,
  pageSize = 20,
}) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({});
  const [hasNextPage, setHasNextPage] = useState(false);
  // Options the user has actually chosen, kept regardless of which page is
  // loaded, so a selection never degrades into a raw id.
  const [chosen, setChosen] = useState({});

  const {loading} = useQuery(query, {
    variables: buildVariables(page, search, pageSize),
    skip,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      const {rows = [], hasNextPage: more = false} = readPage(data) ?? {};
      setPages(current => ({...current, [page]: rows.map(toOption)}));
      setHasNextPage(more);
    },
  });

  // A new search is a new list, not more of the old one.
  const runSearch = useMemo(
    () =>
      _.debounce(value => {
        setPages({});
        setPage(1);
        setSearch(value.trim());
      }, SEARCH_DEBOUNCE),
    [],
  );

  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const options = useMemo(() => {
    const seen = new Set();
    const merged = [];
    Object.keys(pages)
      .sort((a, b) => Number(a) - Number(b))
      .forEach(key => {
        pages[key].forEach(option => {
          if (option?.value && !seen.has(option.value)) {
            seen.add(option.value);
            merged.push(option);
          }
        });
      });
    return merged;
  }, [pages]);

  const onPopupScroll = useCallback(
    event => {
      const {scrollTop, scrollHeight, clientHeight} = event.target;
      if (scrollHeight - scrollTop - clientHeight > 48) return;
      if (loading || !hasNextPage) return;
      setPage(current => current + 1);
    },
    [loading, hasNextPage],
  );

  const remember = useCallback(
    value => {
      const values = Array.isArray(value) ? value : [value];
      setChosen(current => {
        const next = {...current};
        values.filter(Boolean).forEach(one => {
          const match = options.find(option => option.value === one);
          if (match) next[one] = match;
        });
        return next;
      });
    },
    [options],
  );

  /** The list to render: the loaded pages plus any selection not on them. */
  const withSelected = useCallback(
    value => {
      const values = (Array.isArray(value) ? value : [value]).filter(Boolean);
      const onPage = new Set(options.map(option => option.value));
      const missing = values
        .filter(one => !onPage.has(one))
        .map(one => chosen[one] ?? {value: one, label: one});
      return [...missing, ...options];
    },
    [options, chosen],
  );

  return {
    options,
    loading,
    hasNextPage,
    onSearch: runSearch,
    onPopupScroll,
    remember,
    withSelected,
    reset: () => {
      setPages({});
      setPage(1);
      setSearch('');
    },
  };
};

export const PaginatedSelect = ({
  value,
  onChange,
  mode,
  placeholder,
  disabled,
  pagination,
  notFound = 'No matches',
  renderOption,
}) => {
  const {loading, onSearch, onPopupScroll, remember, withSelected} = pagination;
  const optionsToRender = withSelected(value);
  const seen = useRef();
  seen.current = optionsToRender;

  return (
    <Select
      mode={mode}
      value={value ?? undefined}
      onChange={next => {
        remember(next);
        onChange?.(next);
      }}
      showSearch
      allowClear
      disabled={disabled}
      loading={loading}
      onSearch={onSearch}
      onPopupScroll={onPopupScroll}
      // Search is served by the API; filtering again here would hide rows the
      // server deliberately returned.
      filterOption={false}
      placeholder={placeholder}
      notFoundContent={loading ? <Spin size="small" /> : notFound}
      optionLabelProp="label">
      {optionsToRender.map(option => (
        <Select.Option
          key={option.value}
          value={option.value}
          label={option.label}>
          {renderOption ? renderOption(option) : option.label}
        </Select.Option>
      ))}
    </Select>
  );
};

export default PaginatedSelect;
